/**
 * Client-side session state: storage, expiry, and refresh.
 *
 * Deliberately framework-free — no React — so the API client (a plain module)
 * and the auth context can share one source of truth. Two stores would drift
 * the moment a token was refreshed by a fetch that the React tree did not
 * initiate, which is exactly when it matters.
 *
 * WHERE TOKENS LIVE. In localStorage, which means an XSS bug can read them.
 * The stronger option is an httpOnly cookie, and it was considered and not
 * taken: the SPA is served from Vercel while the API is on another origin, so
 * cookies would need SameSite=None with credentialed CORS, and the API would
 * have to grow cookie parsing and CSRF defence for a browser-only path that
 * its other consumers (VistraPM, the MCP server, the CLI) do not use. Given
 * the access token expires in an hour and the SPA has no other secret worth
 * stealing, this matches what supabase-js itself does by default. Revisit if
 * the SPA is ever served same-origin with the API, where the cookie becomes
 * nearly free.
 */

export interface Session {
  accessToken: string;
  refreshToken: string;
  /** Unix SECONDS (not ms) — the wire format from /auth/login. */
  expiresAt: number;
  user: { id: string; email: string | null; name: string | null };
}

const STORAGE_KEY = 'sc-session';

/**
 * Refresh this many seconds BEFORE the token actually expires.
 *
 * Without a margin, a request that passes the expiry check and then spends 200
 * ms in flight can arrive at the API already expired — a 401 that looks random
 * and is impossible to reproduce locally, where latency is nil. Sixty seconds
 * also absorbs the modest clock skew of a client machine.
 */
const REFRESH_MARGIN_SECONDS = 60;

type Listener = (session: Session | null) => void;

let current: Session | null = null;
let listeners: Listener[] = [];
let loaded = false;

/**
 * The in-flight refresh, if any.
 *
 * THIS IS NOT AN OPTIMISATION — it is required for correctness. Supabase
 * ROTATES refresh tokens: redeeming one returns a new one and invalidates the
 * old. A dashboard that fires six queries at once, all with the same expired
 * access token, would start six refreshes with the same refresh token; the
 * first would succeed and the other five would present a token that had just
 * been spent, get refused, and sign the user out mid-session. Collapsing them
 * onto one promise is what prevents that.
 */
let inFlightRefresh: Promise<Session | null> | null = null;

function readStorage(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    // A shape check, not decoration: a half-written or older-format entry
    // would otherwise surface as a confusing failure on the first API call
    // rather than as "not signed in".
    if (!parsed?.accessToken || !parsed?.refreshToken || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    // Private-browsing modes and disabled site data throw on ACCESS, not just
    // on write. Treated as "not signed in" rather than allowed to break boot.
    return null;
  }
}

function writeStorage(session: Session | null): void {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage full or blocked. The session still works for this page's
    // lifetime from the in-memory copy; it just will not survive a reload.
  }
}

/** The current session, hydrating from storage on first call. */
export function getSession(): Session | null {
  if (!loaded) {
    current = readStorage();
    loaded = true;
  }
  return current;
}

export function setSession(session: Session | null): void {
  current = session;
  loaded = true;
  writeStorage(session);
  for (const l of listeners) l(session);
}

/** Subscribe to sign-in/sign-out. Returns the unsubscribe function. */
export function onSessionChange(listener: Listener): () => void {
  listeners.push(listener);
  return () => { listeners = listeners.filter((l) => l !== listener); };
}

/** True when the token is expired or close enough that it may expire in flight. */
export function isExpired(session: Session, nowMs: number = Date.now()): boolean {
  return session.expiresAt - REFRESH_MARGIN_SECONDS <= Math.floor(nowMs / 1000);
}

/** Test-only: drop all state, including the hydration flag and any listeners. */
export function resetSessionForTests(): void {
  current = null;
  loaded = false;
  listeners = [];
  inFlightRefresh = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* see writeStorage */ }
}

/**
 * Returns a usable access token, refreshing first if the current one is spent.
 * null means "not signed in" — the caller should fall through to whatever
 * unauthenticated behaviour applies, not retry.
 *
 * `refreshFn` is injected rather than imported so this module stays free of
 * the API client, which imports IT. Without that the two would form a cycle.
 */
export async function getValidAccessToken(
  refreshFn: (refreshToken: string) => Promise<Session>,
): Promise<string | null> {
  const session = getSession();
  if (!session) return null;
  if (!isExpired(session)) return session.accessToken;

  const refreshed = await refreshOnce(session.refreshToken, refreshFn);
  return refreshed?.accessToken ?? null;
}

/**
 * Refreshes at most once at a time; concurrent callers await the same promise.
 * See the comment on inFlightRefresh for why this is a correctness property.
 */
export async function refreshOnce(
  refreshToken: string,
  refreshFn: (refreshToken: string) => Promise<Session>,
): Promise<Session | null> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const next = await refreshFn(refreshToken);
      setSession(next);
      return next;
    } catch {
      // A refresh token that will not redeem cannot be recovered from — it is
      // spent, revoked, or the account is gone. Clearing the session is what
      // moves the UI to the sign-in page instead of leaving it retrying a
      // request that will never succeed.
      setSession(null);
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}
